<template>
  <!-- ⭐ A TYPE IS A DISC WITH ITS FIRST LETTER (design 169, his answer
       2026-10-13): the type's colour filled, the name's initial WHITE inside.
       The glyph era (2026-09-28) gave the type a body; the letter gives it a
       NAME — « T » on green reads « Taille » at a glance, where a scissors
       icon had to be learned. An untyped job is a SOLID GREY disc with no
       letter: a dashed ring was a line drawing on the page, and « Sans type »
       is written in ink beside it wherever the name would go.
       ⚠ `size` is the DISC's diameter, never the letter's — the letter
       derives. Never under 18 px: below that the letter stops reading. -->
  <span
    class="inline-flex shrink-0 select-none items-center justify-center rounded-full font-bold leading-none text-white"
    :class="untyped ? 'bg-untyped' : ''"
    :style="{
      width: `${size}px`,
      height: `${size}px`,
      fontSize: `${letterSize}px`,
      backgroundColor: untyped ? undefined : resolvedColor,
    }"
    :title="title"
    :role="title ? 'img' : undefined"
    :aria-label="title"
    data-testid="type-mark"
  >{{ letter }}</span>
</template>

<script setup lang="ts">
// A job type's visual mark — the single owner of "how a type looks", so every
// surface reads a type by id and gets colour + letter consistently.
//   typed   → the name's first letter, white, on a disc of the type's colour
//   untyped → a solid grey disc, no letter (nobody has named the work yet)
// TypeDot (the 8px dot) stays for sectors and other non-type marks.
//
// Resolution: by default colour and letter come from the reactive identity
// registry (typeColor/typeInitial), keyed by `typeId`. Callers that already
// hold the resource (DossierCell, the templates list, the picker preview)
// may pass `color`/`name` to override — `undefined` means "resolve from the
// id", an explicit value (incl. null) wins. A type not yet registered draws a
// disc with no letter: the same eventual consistency the colour already has.
import { computed } from 'vue'
import { NO_TYPE_COLOR } from '@/lib/typeColors'
import { typeColor, typeInitial, initialOf } from '@/lib/typeIdentity'

const props = withDefaults(defineProps<{
  typeId?: string | null
  color?: string | null
  /** The type's name when the caller holds it — the letter is derived here. */
  name?: string | null
  title?: string
  size?: number
}>(), {
  size: 18,
})

// `none` is the synthetic "Sans type" option, not a real id to hash.
const resolvedId = computed(() =>
  props.typeId && props.typeId !== 'none' ? props.typeId : null)

// Untyped = no id to resolve AND nothing handed in. A caller holding a type's
// own colour or name (a picker preview on « Auto », a resource in hand) is
// naming a type — its letter must still draw.
const untyped = computed(() => resolvedId.value === null && !props.color && !props.name)

const resolvedColor = computed(() =>
  props.color !== undefined ? (props.color ?? NO_TYPE_COLOR) : typeColor(resolvedId.value))

const letter = computed(() => {
  if (untyped.value) return ''
  if (props.name !== undefined) return initialOf(props.name)
  return typeInitial(resolvedId.value)
})

// The mock's numbers: 11 px on the 20 px line disc, 13 px on the 28 px subject
// disc — and the same ratio past that (the type page's 36 wears 17). Below 28
// the letter holds at 11: any smaller stops reading, which is also why no
// disc goes under 18.
const letterSize = computed(() => (props.size >= 28 ? Math.round(props.size * 0.46) : 11))
</script>
